import type { MessageDescriptor } from "~/intl";
import { defineMessages, FormattedMessage, useIntl } from "~/intl";

import type { FailureType } from "~/providers/global-error-provider";
import { useGetGlobalErrors, useResetGlobalErrors } from "~/providers/global-error-provider";

import type { ErrorT } from "~/services/api-schemas";

import { comparableString } from "@nephroflow/design-system/utils/string";

import type { ModalDialogDismissProps } from "@nephroflow/design-system/components/modal-dialog";
import { ModalDialog } from "@nephroflow/design-system/components/modal-dialog";

export function ModalDialogDismiss(props: Partial<ModalDialogDismissProps>) {
  const { formatMessage } = useIntl();

  return <ModalDialog.Dismiss title={formatMessage(t.close)} {...props} />;
}

export default function ErrorModal() {
  const { formatMessage } = useIntl();

  const globalError = useGetGlobalErrors();
  const resetError = useResetGlobalErrors();

  const someUnhandled =
    globalError?.failureType === "internal_server_error" || globalError?.failureType === "unhandled_error";

  const open =
    !!globalError &&
    (globalError.handlerType === "fallback_modal" ||
      (globalError.handlerType === "form" &&
        globalError.failureType !== "forbidden" &&
        globalError.failureType !== "unprocessable_content"));

  return (
    <ModalDialog
      open={open}
      onOpenChange={(open) => {
        if (open === false) resetError();
      }}
    >
      <ModalDialog.Content>
        <ModalDialog.Header actions={<ModalDialogDismiss />}>
          <ModalDialog.Title>
            <FormattedMessage {...t.errorTitle} />
          </ModalDialog.Title>
        </ModalDialog.Header>

        <ModalDialog.Body>
          {someUnhandled ? (
            <>
              <a href={window.location.origin} title={formatMessage(t.refreshMessage)}>
                <h5 className="font-t5">
                  <FormattedMessage {...t.refreshMessage} />
                </h5>
              </a>
              <hr className="my-4 border-gray-10" />
            </>
          ) : null}

          {globalError ? (
            globalError.errors?.length ? (
              globalError.errors.map((error: ErrorT, index: number) => (
                <Error key={index} failureType={globalError.failureType} error={error} />
              ))
            ) : (
              <Error failureType={globalError.failureType} />
            )
          ) : null}
        </ModalDialog.Body>
      </ModalDialog.Content>
    </ModalDialog>
  );
}

export function Error({ failureType, error }: { failureType?: FailureType; error?: ErrorT }) {
  const { formatMessage } = useIntl();

  const fallbackTranslations: Partial<Record<FailureType, { title: MessageDescriptor; detail: MessageDescriptor }>> = {
    forbidden: { title: t.forbiddenTitle, detail: t.forbiddenDetail },
    not_found: { title: t.notFoundTitle, detail: t.notFoundDetail },
    unprocessable_content: { title: t.unprocessableContentTitle, detail: t.unprocessableContentDetail },
    method_not_allowed: { title: t.methodNotAllowedTitle, detail: t.methodNotAllowedDetail },
    content_too_large: { title: t.contentTooLargeTitle, detail: t.contentTooLargeDetail },
  };

  const fallback = failureType ? fallbackTranslations[failureType] : undefined;

  let { title, detail } = error || {};

  if (fallback) {
    title ||= formatMessage(fallback.title);
    detail ||= formatMessage(fallback.detail);
  } else {
    title ||= formatMessage(t.unknownError);
  }

  if (comparableString(title) === comparableString(detail || "")) {
    title = formatMessage(fallback?.title || t.unknownError);
  }

  if (!detail) {
    return <p role="alert">{title}</p>;
  }

  return (
    <p role="alert">
      <strong>{title}</strong>
      <br />
      {detail}
    </p>
  );
}

const t = defineMessages({
  errorTitle: {
    id: "error_modal_title",
    defaultMessage: "An error has occurred",
  },
  refreshMessage: {
    id: "error_modal_refresh",
    defaultMessage: "Please refresh the app",
  },
  unknownError: {
    id: "error_modal_unknown_error",
    defaultMessage: "An unknown error",
  },
  forbiddenTitle: {
    id: "error_modal_forbidden_title",
    defaultMessage: "Insufficient rights",
  },
  forbiddenDetail: {
    id: "error_modal_forbidden_detail",
    defaultMessage: "You do not have the required rights to use this feature.",
  },
  notFoundTitle: {
    id: "error_modal_not_found_title",
    defaultMessage: "Not found",
  },
  notFoundDetail: {
    id: "error_modal_not_found_detail",
    defaultMessage: "The requested resource was not found.",
  },
  unprocessableContentTitle: {
    id: "error_modal_unprocessable_content_title",
    defaultMessage: "Unprocessable Content",
  },
  unprocessableContentDetail: {
    id: "error_modal_unprocessable_content_detail",
    defaultMessage: "The operation could not be processed",
  },
  methodNotAllowedTitle: {
    id: "error_modal_method_not_allowed_title",
    defaultMessage: "Not allowed",
  },
  methodNotAllowedDetail: {
    id: "error_modal_method_not_allowed_detail",
    defaultMessage: "This operation is not allowed!",
  },
  contentTooLargeTitle: {
    id: "error_modal_payload_too_large_title",
    defaultMessage: "Payload too large",
  },
  contentTooLargeDetail: {
    id: "error_modal_payload_too_large_detail",
    defaultMessage: "The passed attachments or files are too big!",
  },
  close: {
    id: "error_dialog_close",
    defaultMessage: "Close",
  },
});
